import { useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  House,
  CreditCard,
  LayoutDashboard,
  Settings2,
  Shield,
  Sparkles,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useLocks, usePermissions, useTranslation } from '@bms/common';
import { useMemo } from 'react';
import { useFlavour } from '@/providers/FlavourProvider';
import { usesGrmsDashboard } from '@/flavours';
import { useDeskAttentionCount } from '@/components/desk/deskAttentionCount';
import { useActivityUnread } from '@/components/activity/useActivityUnread';
import { hasSharedAreaLock } from '@/components/activity/sharedAreaLocks';
import { cn } from '@/lib/utils';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from '@/components/ui/sidebar';
import { frontOfficeNavEntries } from '@/pages/frontOffice/frontOfficeView';
import { sidebarOrder } from '@/lib/housekeepingMode';

// ============================================================================
// Types
// ============================================================================

interface NavEntry {
  key: string;
  path: string;
  icon: LucideIcon;
  label: string;
  /** A count beside the label; `null` shows none. */
  count?: number | null;
  /** `alert` (the Desk's queue) reads red; `quiet` (Activity's unread) reads muted. */
  countTone?: 'alert' | 'quiet';
  /** The count's accessible name. */
  countLabel?: string;
}

// ============================================================================
// NavLinks — the sidebar
//
// Two groups. The unlabelled first one is what every operator sees, gated by
// the site's flavour; **Front office** is the supervisor tier of BMS-spec
// `docs/grms/specification.md` §14.2, gated per entry on permission codes read
// from the server's own projection.
//
// Never on a role name: a site that regrants `cards:read` moves the Cards page
// with it, and `site_admin` — which holds every code in the group — sees the
// whole group without being named anywhere. The gate fails closed, so a
// projection that has not been read yet shows no group rather than a page that
// will answer 403.
// ============================================================================

const FRONT_OFFICE_ICONS: Record<string, LucideIcon> = {
  visitors: Users,
  cards: CreditCard,
  reports: BarChart3,
  'hotel-settings': Settings2,
};

export function NavLinks() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { features, flavour } = useFlavour();
  const permissions = usePermissions();
  const isDesk = usesGrmsDashboard(flavour);
  // The Attention queue's length while the Desk is on screen (plan-frontDeskUx D3).
  const attentionCount = useDeskAttentionCount();
  const onActivity = location.pathname.startsWith('/activity');
  // Rows since this browser last had Activity open; none while it is open.
  const activityUnread = useActivityUnread();
  // UX-17: a site whose every lock is a guest-room door has nothing on the
  // Access Control page, so the entry shows only once a shared-area lock is
  // known (the page's own empty state points at Activity's Doors filter).
  const { data: locks } = useLocks();
  const showAccessControl = features.hasAccessControl && !!locks && hasSharedAreaLock(locks);

  // Which entries and in what order is `sidebarOrder`'s decision, from the
  // permission projection alone (F3, R3); this only dresses them.
  const items: NavEntry[] = useMemo(
    () =>
      sidebarOrder(permissions.has, { hasHousekeeping: features.hasHousekeeping, showAccessControl }).map(
        ({ key, path }): NavEntry => {
          switch (key) {
            case 'dashboard':
              return {
                key,
                path,
                icon: isDesk ? House : LayoutDashboard,
                label: isDesk ? t('nav.desk') : t('nav.dashboard'),
                count: isDesk ? attentionCount : null,
                countLabel: t('grms.desk.countAria', { count: attentionCount ?? 0 }),
              };
            case 'activity':
              return {
                key,
                path,
                icon: Activity,
                label: t('nav.activity'),
                count: onActivity ? null : activityUnread,
                countTone: 'quiet',
                countLabel: t('activity.unreadAria', { count: activityUnread ?? 0 }),
              };
            case 'access-control':
              return { key, path, icon: Shield, label: t('nav.accessControl') };
            case 'housekeeping':
              return { key, path, icon: Sparkles, label: t('nav.housekeeping') };
          }
        },
      ),
    [t, permissions, features, isDesk, attentionCount, onActivity, activityUnread, showAccessControl]
  );

  // `permissions.has` is stable per answer rather than per render, so the list
  // is recomputed when the projection changes and not on every keystroke
  // elsewhere in the shell.
  const frontOffice: NavEntry[] = useMemo(
    () =>
      frontOfficeNavEntries(permissions.has).map((entry) => ({
        key: entry.key,
        path: entry.path,
        icon: FRONT_OFFICE_ICONS[entry.key] ?? CreditCard,
        label: t(`nav.${entry.key}`),
      })),
    [permissions, t],
  );

  const isActive = (path: string) => {
    // The Desk is `/` or, for the supervisor home, `/dashboard`: either is it.
    if (path === '/' || path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard' || location.pathname.startsWith('/rooms/');
    }
    return location.pathname.startsWith(path);
  };

  const renderItem = (item: NavEntry) => (
    <SidebarMenuItem key={item.key}>
      <SidebarMenuButton
        isActive={isActive(item.path)}
        tooltip={item.label}
        onClick={() => navigate(item.path)}
        className={cn(
          // The active item is marked by a 3 px brand bar on the
          // leading edge over a faint brand wash, not by the default
          // grey accent fill — that fill is also the hover state, so
          // "current page" and "the pointer is here" looked alike.
          'relative',
          'data-[active=true]:bg-primary/8 data-[active=true]:text-sidebar-foreground',
          'data-[active=true]:before:absolute data-[active=true]:before:inset-y-1',
          'data-[active=true]:before:left-0 data-[active=true]:before:w-[3px]',
          'data-[active=true]:before:rounded-full data-[active=true]:before:bg-primary',
        )}
      >
        <item.icon />
        <span>{item.label}</span>
        {item.count != null && item.count > 0 && (
          <span
            className={cn(
              'ml-auto text-caption font-semibold tnum group-data-[collapsible=icon]:hidden',
              item.countTone === 'quiet' ? 'text-muted-foreground' : 'text-destructive-text',
            )}
            aria-label={item.countLabel}
          >
            {item.count}
          </span>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  return (
    <>
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>{items.map(renderItem)}</SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {/* The group appears only when at least one of its entries does. */}
      {frontOffice.length > 0 && (
        <SidebarGroup>
          <SidebarGroupLabel>{t('nav.frontOffice')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>{frontOffice.map(renderItem)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}
    </>
  );
}
